import { useState, type ReactNode } from "react";
import { formatText } from "../i18n/texts";

// Calendario de rango (RangeCalendar.razor de Bheli.Web.Feedback, C34 «Elegir fechas»): mismo HTML. Escritorio: dos
// meses, ‹ en el primero y › en el segundo; celular: un mes. Los días son aaaa-mm-dd (DateOnly) y «hoy» lo da quien lo usa.

export type Day = string;

const pad = (value: number) => String(value).padStart(2, "0");
export const dayOf = (year: number, month: number, date: number): Day => `${year}-${pad(month)}-${pad(date)}`;
const parts = (day: Day) => day.split("-").map(Number) as [number, number, number];
const utc = (day: Day) => {
  const [year, month, date] = parts(day);
  return new Date(Date.UTC(year, month - 1, date));
};
const dayNumber = (day: Day) => Math.round(utc(day).getTime() / 86_400_000);
const addMonths = (first: Day, count: number): Day => {
  const [year, month] = parts(first);
  const index = year * 12 + (month - 1) + count;
  return dayOf(Math.floor(index / 12), (index % 12) + 1, 1);
};
const daysInMonth = (year: number, month: number) => new Date(Date.UTC(year, month, 0)).getUTCDate();

// Los nombres como los da .NET en el navegador: «octubre 2026» y «LU MA …» (es), «October 2026» y «Mo Tu …» (en).
const esMonths = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "setiembre", // es-PE, como el chat y Estadísticas (K2 · referencia «Setiembre 2026»)
  "octubre",
  "noviembre",
  "diciembre",
];
const enMonths = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const esDays = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const enDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const esShortest = ["DO", "LU", "MA", "MI", "JU", "VI", "SA"];
const enShortest = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
// es-PE fijo: los datos ICU reducidos del navegador daban «sept»; el servidor «set.».
const spanishShort = ["ene.", "feb.", "mar.", "abr.", "may.", "jun.", "jul.", "ago.", "set.", "oct.", "nov.", "dic."];
const englishShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const english = (language: string) => language === "en-US";

/** «D» de .NET: «sábado, 24 de octubre de 2026» o «Saturday, October 24, 2026». */
export function longDate(day: Day, language: string): string {
  const [year, month, date] = parts(day);
  const weekday = utc(day).getUTCDay();
  return english(language)
    ? `${enDays[weekday]}, ${enMonths[month - 1]} ${date}, ${year}`
    : `${esDays[weekday]}, ${date} de ${esMonths[month - 1]} de ${year}`;
}

/** El rango corto del pie y de la píldora de Período («17 set. – 16 oct.»); con años distintos, con el año. */
export function rangeSpan(from: Day, to: Day, language: string): string {
  const year = parts(from)[0] !== parts(to)[0];
  const short = (day: Day) => {
    const [y, m, d] = parts(day);
    return english(language)
      ? `${d} ${englishShort[m - 1]}${year ? ` ${y}` : ""}`
      : `${d} ${spanishShort[m - 1]}${year ? ` ${y}` : ""}`;
  };
  return `${short(from)} – ${short(to)}`;
}

type Props = {
  language: string;
  /** Sin rango aplicado, null: el calendario abre sin días elegidos, solo el anillo de hoy (C34 1.14.4). */
  from: Day | null;
  to: Day | null;
  today: Day;
  /** Días como máximo (Estadísticas: 366); null, sin límite. */
  maxDays?: number | null;
  inline?: boolean;
  previousLabel: string;
  nextLabel: string;
  cancelLabel?: string;
  applyLabel?: string;
  tooLongLabel?: string;
  dayOneLabel: string;
  daysManyLabel: string;
  previousArrow: ReactNode;
  nextArrow: ReactNode;
  onApplied?: (from: Day, to: Day) => void;
  onChanged?: (from: Day, to: Day) => void;
  onCancelled?: () => void;
};

export function RangeCalendar(props: Props) {
  const { language, today } = props;
  const maxDays = props.maxDays === undefined ? 366 : props.maxDays;
  const [start, setStart] = useState<Day | null>(props.from);
  const [end, setEnd] = useState<Day | null>(props.to ?? props.from);
  const [hasEnd, setHasEnd] = useState(props.from !== null);
  // K2 · C34 1.14.5: `month` es el mes de la derecha (el actual, o el del fin del rango aplicado); a su izquierda, el
  // anterior. Nunca un mes futuro entero. Donde entra un solo mes (celular), se ve el de la derecha.
  const [month, setMonth] = useState(() => {
    const base = props.to ?? props.from ?? today;
    return dayOf(parts(base)[0], parts(base)[1], 1);
  });
  const invalid = maxDays !== null && start !== null && end !== null && dayNumber(end) - dayNumber(start) >= maxDays;
  const canNext = month < dayOf(parts(today)[0], parts(today)[1], 1);

  const select = (day: Day) => {
    if (!hasEnd && start !== null) {
      let first = start,
        last = day;
      if (last < first) [first, last] = [last, first];
      setStart(first);
      setEnd(last);
      setHasEnd(true);
      if (props.inline && !(maxDays !== null && dayNumber(last) - dayNumber(first) >= maxDays))
        props.onChanged?.(first, last);
    } else {
      setStart(day);
      setEnd(day);
      setHasEnd(false);
      if (props.inline) props.onChanged?.(day, day);
    }
  };
  const bandClass = (day: Day, column: number) => {
    if (start === null || end === null || day < start || day > end) return "stat-calendar-day";
    const [year, monthNumber, date] = parts(day);
    const left = day === start || column === 0 || date === 1;
    const right = day === end || column === 6 || date === daysInMonth(year, monthNumber);
    return "stat-calendar-day band" + (left ? " band-start" : "") + (right ? " band-end" : "");
  };
  const dotClass = (day: Day) => {
    const classes = ["stat-calendar-dot"];
    if (day === start || (day === end && hasEnd)) classes.push("edge");
    else if (day === today) classes.push("today");
    if (day > today) classes.push("future");
    return classes.join(" ");
  };
  const summary = () => {
    if (start === null || end === null) return "";
    const days = dayNumber(end) - dayNumber(start) + 1;
    const span = rangeSpan(start, end, language);
    const count = new Intl.NumberFormat(english(language) ? "en-US" : "es-PE").format(days);
    return days <= 1 ? `${span} · ${props.dayOneLabel}` : `${span} · ${formatText(props.daysManyLabel, count)}`;
  };
  const shortest = english(language) ? enShortest : esShortest;

  return (
    <div className={`stat-range stat-range-pair ${props.inline ? "stat-range-inline" : ""}`}>
      <div className="stat-range-calendars">
        {[-1, 0].map((offset) => {
          const shown = addMonths(month, offset);
          const [year, monthNumber] = parts(shown);
          const lead = (utc(shown).getUTCDay() + 6) % 7;
          const cells: { day: Day | null; column: number }[] = [];
          for (let i = 0; i < lead; i++) cells.push({ day: null, column: i });
          for (let d = 1; d <= daysInMonth(year, monthNumber); d++)
            cells.push({ day: dayOf(year, monthNumber, d), column: (lead + d - 1) % 7 });
          return (
            // «second»: el mes que se esconde donde entra uno solo (las reglas del celular ya lo llaman así).
            <div key={offset} className={`stat-range-month ${offset === -1 ? "second" : ""}`}>
              <header>
                <button
                  type="button"
                  className={offset === 0 ? "stat-range-prev-mobile" : undefined}
                  aria-label={props.previousLabel}
                  onClick={() => setMonth(addMonths(month, -1))}
                >
                  {props.previousArrow}
                </button>
                <strong>{`${(english(language) ? enMonths : esMonths)[monthNumber - 1]} ${year}`}</strong>
                {offset === 0 ? (
                  <button
                    type="button"
                    aria-label={props.nextLabel}
                    onClick={() => setMonth(addMonths(month, 1))}
                    disabled={!canNext}
                  >
                    {props.nextArrow}
                  </button>
                ) : null}
              </header>
              <div className="stat-calendar-weekdays" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5, 6].map((dow) => (
                  <span key={dow}>{shortest[(dow + 1) % 7]}</span>
                ))}
              </div>
              <div className="stat-calendar-grid">
                {cells.map(({ day, column }, index) =>
                  day === null ? (
                    <span key={index} className="stat-calendar-day" data-part="dia" aria-hidden="true">
                      <span className="stat-calendar-dot" data-part="dia-numero"></span>
                    </span>
                  ) : (
                    <span key={index} className={bandClass(day, column)} data-part="dia">
                      <button
                        type="button"
                        className={dotClass(day)}
                        data-part="dia-numero"
                        aria-label={longDate(day, language)}
                        aria-pressed={start !== null && end !== null && day >= start && day <= end ? ("" as "true") : undefined}
                        disabled={day > today}
                        onClick={() => select(day)}
                      >
                        {parts(day)[2]}
                      </button>
                    </span>
                  ),
                )}
              </div>
            </div>
          );
        })}
      </div>
      {invalid ? <p role="alert">{props.tooLongLabel}</p> : null}
      <footer className="stat-range-foot">
        <span data-part="resumen">{summary()}</span>
        {!props.inline ? (
          <>
            <button
              type="button"
              className="button secondary"
              data-part="btn-secundario"
              onClick={() => props.onCancelled?.()}
            >
              {props.cancelLabel}
            </button>
            <button
              type="button"
              className="button"
              data-part="btn-principal"
              disabled={invalid}
              onClick={() => {
                if (!invalid && start !== null && end !== null) props.onApplied?.(start, end);
              }}
            >
              {props.applyLabel}
            </button>
          </>
        ) : null}
      </footer>
    </div>
  );
}
